import axios, { AxiosError, isAxiosError, isCancel } from 'axios';
import { PROFILE_PATH, SILENT_LOGIN_PATH } from '@/app/api/auth/[auth0]/constants';
import { logSentryException } from '../utils/sentry';

export type { AxiosError };

export { isAxiosError, isCancel };

export const ROUTE = {
  LINK_PREVIEW_MEDIA: '/link-preview-media',
  DEMO: {
    GET_DATASETS: '/demo/get-datasets',
    GET_MESSAGES: '/demo/get-messages',
  },
  DASHBOARD: {
    REPORTS_SUMMARY: '/dashboard/reports-summary',
    REPORTS_SUMMARY_RANGES: '/dashboard/reports-summary/ranges',
    REPORTS_SECTIONS: '/dashboard/reports-sections',
  },
  USER: {
    CURRENT: '/user/current',
    ORGANIZATIONS: '/user/organizations',
    ORGANIZATION: '/user/organization',
    SUBSCRIPTIONS: '/user/subscriptions',
    SUBSCRIPTIONS_AVAILABLE_PRODUCT_IDS: '/user/subscriptions/availableProducts',
    SILENT_LOGIN: `/api/auth/${SILENT_LOGIN_PATH}`,
    REVALIDATE_SESSION: `/auth/${PROFILE_PATH}`,
    REFRESH_TOKEN: '/auth/refresh', // pages router
    UPDATE_SESSION: '/auth/update-session', // pages router
    USER_COMPANY: '/user/company',
  },
  PAYMENTS: {
    CHECKOUT: '/payments/stripe/checkout',
    CHECKOUT_SETUP: '/payments/stripe/checkout/setup',
    CHECKOUT_TRIALING: '/payments/stripe/checkout/trialing',
    BILLING: '/payments/stripe/billing',
    PRODUCT_DATA: '/payments/stripe/product-data',
    SUBSCRIPTION: '/payments/stripe/subscription',
  },
  COPILOT: {
    QUERY_MAP: '/map-events/query-copilot',
    QUERY_REPORTS: '/copilot/reports',
    QUERY_REPORTS_DEEP_RESEARCH: '/copilot/reports/research',
    QUERY_GI_ACTIONS: '/copilot/geopolitical-actions',
    GI_ACTIONS_STREAM: '/copilot/geopolitical-actions/stream',
    GI_ACTIONS_SESSION: '/copilot/geopolitical-actions/session',
    GI_ACTIONS_SESSION_SIMULATE: '/copilot/geopolitical-actions/session/simulate',
    GI_ACTIONS_SESSION_SIMULATE_STREAM: '/copilot/geopolitical-actions/session/simulate/stream',
    GI_ACTIONS_SESSION_ADVANCE_ROUND: '/copilot/geopolitical-actions/session/advance-round',
  },
  MAP_EVENTS: {
    ASSETS: '/map-events/get-assets',
    INCIDENTS: '/map-events/get-incidents',
    INCIDENTS_TAGS: '/map-events/get-incidents-tags',
    INCIDENTS_TAG_COLORS: '/map-events/get-incidents-tag-colors',
    ASSET_DECISIONS: '/map-events/get-asset-decisions',
    /** @deprecated */
    INCIDENTS_QUERY: '/map-events/query-incidents',
    INCIDENTS_FEED: '/map-events/get-incidents-feed',
    INCIDENTS_COUNT: '/map-events/get-incidents-count',
    INCIDENT_TYPES: '/map-events/incident-types',
    INCIDENT_IMAGE: '/map-events/incident-image',
    INCIDENT_LOCATIONS: '/map-events/incident-locations',
    SEVERITY_LEVELS: '/map-events/severity-levels',
    INCIDENT_VERIFICATION_SOURCES: '/map-events/incident-verification-sources',
    INCIDENT_DISTINCT_FIELD_VALUES: '/map-events/incident-distinct-field-values',
    TARGET_PHOTO: '/map-events/get-target-photo',
  },
  REPORTS: {
    LIST: '/reports/list',
    UPLOAD: '/reports/upload',
    DELETE: '/reports/delete',
    RENAME: '/reports/rename',
    DOWNLOAD_URL: '/reports/download-url',
    THUMBNAIL: '/reports/thumbnail',
    PDF_HTML: '/reports/pdf-html',
  },
  NARRATIVES: {
    RECORDS: '/narratives/records',
    RELATIONS: '/narratives/relations',
    RELATION_MESSAGES: '/narratives/relation-messages',
    FILES: '/narratives/files',
    SOURCE: '/narratives/source',
    NODE_NARRATIVES: '/narratives/node-narratives',
    FAVOURITE_NODES: '/narratives/favourite-nodes',
    NODE_CUSTOMIZATIONS: '/narratives/node-customizations',
  },
  TRADE: {
    FLOWS: '/trade/flows',
    FLOWS_YEARS: '/trade/flows/years',
    GEOPOLITICAL_TRADERS: '/trade/geopolitical-traders',
  },
  GEOPOLITICAL_ACTIONS: {
    INCIDENTS: '/geopolitical-actions/incidents',
    INCIDENT_COUNTRIES: '/geopolitical-actions/incidents/countries',
    ACTORS: '/geopolitical-actions/actors',
    ACTORS_ROUND: '/geopolitical-actions/actors/round',
    ACTORS_RESET: '/geopolitical-actions/actors/reset',
    ROUNDS: '/geopolitical-actions/rounds',
  },
  CACHE: {
    USER: '/cache/user',
    ORGANIZATION: '/cache/organization',
  },
  INCIDENTS: {
    STREAM: (orgId: string) => `/api/incidents/stream?orgId=${encodeURIComponent(orgId)}`,
    COLOR_SCHEME: '/incidents/color-scheme',
    DATE_TIME_RANGE: '/incidents/date-time-range',
    ASSET_GSI: '/incidents/asset-gsi',
    REGION_GSI: '/incidents/region-gsi',
    REGION_GSI_TREND: '/incidents/region-gsi-trend',
  },
  SEARCH_CONSOLE: {
    LIST: '/search-console',
    DATE_TIME_RANGE: '/search-console/date-time-range',
    ALIGNMENT_FILTERS: '/search-console/alignment-filters',
    FAVOURITES: '/search-console/favourites',
    FAVOURITE_CATEGORIES: '/search-console/favourite-categories',
    FAVOURITE_SHARES: '/search-console/favourite-shares',
    SHARED_FAVOURITES: '/search-console/shared-favourites',
    EXPORT_PDF: '/search-console/export-pdf',
    KEYWORD_TIMESERIES: '/search-console/keyword-timeseries',
    SEARCH_HISTORY: '/search-console/search-history',
  },
  INTERCOM: {
    JWT: '/intercom',
  },
  EMBEDDED_APPS: {
    TOKEN: (appId: string) => `/embedded-apps/${encodeURIComponent(appId)}/token`, // pages router
  },
};

export const readAxiosError = (error: AxiosError | Error) => {
  const data = isAxiosError(error) ? (error.response?.data as { error?: string; message?: string }) : {};
  const message = data?.error || data?.message || error.message;
  return typeof message === 'string' ? message : JSON.stringify(message);
};

export const axiosInstance = axios.create({
  baseURL: '/api',
  timeout: 60 * 1000,
});

axiosInstance.interceptors.request.use((config) => {
  // config.headers.Authorization = ''
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    let payload: string = error?.message;
    try {
      payload = JSON.stringify(error.response?.data || error);
    } catch (err) {
      //
    }

    logSentryException({
      message: payload,
      place: 'internal-api',
      data: {
        stack: error.stack,
        status: error.status || error.response?.status,
        message: error.message,
        code: error.code,
      },
    });

    return Promise.reject(error);
  },
);
